import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import updateLocale from "dayjs/plugin/updateLocale";
import { useEffect, useMemo, useRef, useState } from "react";

import { API, pathRegex } from "./api/api";
import { LoaderSpinner } from "./components/LoaderSpinner";
import { GIFT_CARD_PATHS } from "./constants";
import { FLOW_TYPES } from "./constants/flowType";
import Routes from "./routes";
import { useTicketingContext } from "./store";
import { ACTION_TYPES } from "./store/actionTypes";

const App = (appData = {}) => {
  const { state, dispatch } = useTicketingContext();
  const [initNewCart, setInitNewCart] = useState(false);
  const [restoringCart, setRestoringCart] = useState(true);
  const previousLoginState = useRef(null);
  const {
    addons,
    summary,
    texts,
    current_currency,
    currencies,
    appData: ctxAppData,
  } = state;
  const { restoredAddons } = summary;
  const productId = "900";
  const locale = "da_DK";

  useEffect(() => {
    dispatch({ type: ACTION_TYPES.APP_SET_DATA, payload: { data: appData } });
  }, [dispatch, appData]);

  const {
    refetch: refetchLoginSession,
    data: loginSessionData,
    error: loginSessionError,
  } = useQuery({
    queryKey: ["checkLoginSession"],
    queryFn: () => API.checkUserLoginSession(),
    enabled: false,
    refetchOnWindowFocus: true,
    retry: false,
  });

  useEffect(() => {
    if (loginSessionData !== undefined) {
      dispatch({
        type: ACTION_TYPES.SET_LOGIN_SESSION_DATA,
        payload: { data: loginSessionData },
      });
    }
  }, [loginSessionData]);

  useEffect(() => {
    if (loginSessionError) {
      dispatch({
        type: ACTION_TYPES.SET_LOGIN_SESSION_DATA,
        payload: { data: null },
      });
    }
  }, [loginSessionError]);

  useEffect(() => {
    refetchLoginSession();
  }, [state.stepper?.active, refetchLoginSession]);

  useEffect(() => {
    const currentLoginState = state.loginSessionData?.loggedIn;

    if (previousLoginState.current === true && currentLoginState === false) {
      window.location.reload();
    }

    previousLoginState.current = currentLoginState;
  }, [state.loginSessionData]);

  const selectedCurrency = useMemo(() => {
    if (!current_currency || !Array.isArray(currencies)) {
      return null;
    }
    return currencies.find((c) => c.name === current_currency) || null;
  }, [current_currency, currencies]);

  useEffect(() => {
    if (selectedCurrency) {
      dispatch({
        type: ACTION_TYPES.SET_SELECTED_CURRENCY,
        payload: { selectedCurrency },
      });
    }
  }, [dispatch, selectedCurrency]);

  const isGiftCardPath = useMemo(() => {
    const match = window.location.pathname.match(pathRegex) || [];
    const currentPath = (match[2] || "").replace(/\/$/, "");
    const firstSegment = currentPath.split("/")[0];
    return GIFT_CARD_PATHS.has(firstSegment);
  }, []);

  const giftCardInitPath = useMemo(() => {
    const match = window.location.pathname.match(pathRegex) || [];
    const firstSegment = (match[2] || "").replace(/\/$/, "").split("/")[0];
    return firstSegment === "gavekort" ? "billet" : "ticket";
  }, []);

  useEffect(() => {
    const session = state.loginSessionData;
    if (session === null || session === undefined) {
      return;
    }
    if (isGiftCardPath) {
      return;
    }

    const match = window.location.pathname.match(pathRegex) || [];
    const locale = match[1] || "en";
    const currentPath = (match[2] || "").replace(/\/$/, "");

    if (session.redirectUrl && currentPath !== session.redirectUrl) {
      window.location.href = `/${locale}/${session.redirectUrl}`;
    }
  }, [state.loginSessionData, isGiftCardPath]);

  const initQuery = useQuery({
    queryKey: ["init", productId, isGiftCardPath],
    queryFn: () =>
      API.getInitialData(888, isGiftCardPath ? giftCardInitPath : undefined),
    enabled: Boolean(productId),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  useEffect(() => {
    if (!initQuery.data) {
      return;
    }
    const data = initQuery.data;
    const isGiftCard = isGiftCardPath;

    let flowType = FLOW_TYPES.ADMISSION;
    if (isGiftCard) {
      flowType = FLOW_TYPES.GIFT_CARD;
    } else if (data?.data?.admission_tickets?.length === 0) {
      flowType = FLOW_TYPES.ACTIVITIES_ONLY;
    }
    data.data.flowType = flowType;
    if (isGiftCard) {
      data.data.is_gift_card = true;
    }

    const tickets = data?.data?.admission_tickets || [];
    const minTickets = tickets
      .filter((t) => t.min_limit > 0)
      .map((t) => ({
        id: t.id,
        productSku: t.product_sku,
        qty: t.min_limit,
        admission: t.admission,
      }));

    dispatch({
      type: ACTION_TYPES.APP_SET_DATA,
      payload: { ...data, minTickets },
    });
  }, [initQuery.data]);

  useEffect(() => {
    const locale = state?.locale;
    const dayNames = texts?.common?.day_names;
    const monthNames = texts?.common?.month_names;
    if (!locale || !dayNames || !monthNames) {
      return;
    }

    dayjs.extend(updateLocale);
    dayjs.updateLocale(locale, {
      weekdays: dayNames,
      months: monthNames,
    });
  }, [state?.locale, texts?.common?.day_names, texts?.common?.month_names]);

  useEffect(() => {
    const restoreCart = async (cartMask) => {
      try {
        const restoredCart = await API.restoreCart(cartMask);

        const isCartNotFound =
          !restoredCart?.ok ||
          restoredCart?.status === 404 ||
          restoredCart?.data?.error === "Cart not found";

        if (isCartNotFound) {
          console.warn("Cart not found on backend, initiating new cart");

          try {
            const newCart = await API.initiateCart();
            const newCartMask = newCart?.data?.cartMask || "";

            try {
              localStorage.setItem("cart", newCartMask);
            } catch (err) {
              console.error("Failed to save new cart to localStorage:", err);
            }

            dispatch({
              type: ACTION_TYPES.APP_SET_DATA,
              payload: { ...newCart },
            });

            setRestoringCart(false);
            setInitNewCart(false);
            return;
          } catch (err) {
            console.error(
              "Failed to initiate new cart after stale restore:",
              err
            );
            setInitNewCart(true);
            return;
          }
        }

        if (restoredCart?.data?.message || restoredCart?.data?.length === 0) {
          setInitNewCart(true);
          return;
        }

        const normalizeLocale = (l) =>
          l && (l.toLowerCase() === "da" || l.toLowerCase() === "danish")
            ? "da"
            : "en";
        const urlLocale = normalizeLocale(
          window.location.pathname.match(pathRegex)?.[1] || "en"
        );
        const cartPermalinkUrl = restoredCart?.data?.cart?.checkoutUrl;
        let restoredCheckoutUrl = null;
        if (cartPermalinkUrl) {
          try {
            const parsed = new URL(cartPermalinkUrl);
            const cartToken = (cartMask.split("/").pop() ?? "").split("?")[0];
            const builtUrl = new URL(
              `/checkouts/cn/${cartToken}/${urlLocale}`,
              `https://${parsed.hostname}`
            );
            restoredCheckoutUrl = builtUrl.toString();
          } catch {}
        }

        dispatch({
          type: ACTION_TYPES.APP_SET_DATA,
          payload: {
            data: {
              cartMask,
              ...(restoredCheckoutUrl
                ? { checkoutUrl: restoredCheckoutUrl }
                : {}),
            },
          },
        });
        dispatch({
          type: ACTION_TYPES.RESTORE_CART,
          payload: { ...restoredCart },
        });
        setRestoringCart(false);
      } catch (error) {
        console.error("Failed to restore cart:", error);
        // If restore fails, initiate a new cart
        setInitNewCart(true);
      }
    };

    if (initQuery.isInitialLoading) {
      return;
    }

    try {
      const previousCart = localStorage.getItem("cart");
      if (previousCart) {
        const normalizeLocale = (l) =>
          l && (l.toLowerCase() === "da" || l.toLowerCase() === "danish")
            ? "da"
            : "en";
        const storedLocale = normalizeLocale(
          localStorage.getItem("cartLocale") || "en"
        );
        const urlLocale = normalizeLocale(
          window.location.pathname.match(pathRegex)?.[1] || "en"
        );
        if (storedLocale !== urlLocale) {
          localStorage.removeItem("cart");
          localStorage.removeItem("cartLocale");
          setInitNewCart(true);
          return;
        }

        const error_message = state.appData?.config?.texts?.error_message;
        if (state.is_city_card && error_message && error_message !== "") {
          dispatch({
            type: ACTION_TYPES.SET_ACTIVE_STEP,
            payload: { stepToSet: 0 },
          });
          setRestoringCart(false);
        } else {
          restoreCart(previousCart);
        }
        return;
      }
    } catch (error) {
      console.error("Failed to access localStorage:", error);
    }
    setInitNewCart(true);
  }, [initQuery.isInitialLoading]);

  const initiateCartQuery = useQuery({
    queryKey: ["initCart"],
    queryFn: API.initiateCart,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: initNewCart,
  });

  useEffect(() => {
    if (!initiateCartQuery.data) {
      return;
    }
    const data = initiateCartQuery.data;
    dispatch({ type: ACTION_TYPES.APP_SET_DATA, payload: { ...data } });
    try {
      const localeRaw = (
        window.location.pathname.match(pathRegex)?.[1] || "en"
      ).toLowerCase();
      const locale = localeRaw === "da" || localeRaw === "danish" ? "da" : "en";
      localStorage.setItem("cart", data?.data?.cartMask || "");
      localStorage.setItem("cartLocale", locale);
    } catch (err) {
      console.error("Failed to save cart to localStorage:", err);
    }
    setRestoringCart(false);
  }, [initiateCartQuery.data]);

  useEffect(() => {
    if (!initiateCartQuery.error) {
      return;
    }
    console.error("Failed to initiate cart:", initiateCartQuery.error);
    setRestoringCart(false);
  }, [initiateCartQuery.error]);

  useEffect(() => {
    if (
      Array.isArray(addons) &&
      addons.length > 0 &&
      Array.isArray(restoredAddons) &&
      restoredAddons.length > 0
    ) {
      dispatch({ type: ACTION_TYPES.RESTORE_ADDONS });
    }
  }, [dispatch, addons?.length, restoredAddons?.length]);

  const isLoadingTickets = initQuery.isInitialLoading;
  const isInitiatingCart = initiateCartQuery.isInitialLoading;

  if (isLoadingTickets) {
    return (
      <LoaderSpinner
        isFixedCenter
        label={locale === "da_DK" ? "Indlæser billetter" : "Loading tickets"}
      />
    );
  }
  if (restoringCart) {
    return (
      <LoaderSpinner
        isFixedCenter
        label={texts?.loader?.restoring_cart || "Restoring previous cart"}
      />
    );
  }
  if (isInitiatingCart) {
    return <LoaderSpinner isFixedCenter label="" />;
  }
  if (initQuery.isError) {
    console.error("Failed to load initial ticket data", initQuery.error);
    const baseLabel =
      locale === "da_DK"
        ? "Kunne ikke indlæse billetdata"
        : "Could not load ticket data";
    const status = initQuery.error?.status;
    return (
      <LoaderSpinner
        isFixedCenter
        label={status ? `${baseLabel} (${status})` : baseLabel}
      />
    );
  }

  return <Routes />;
};

export default App;
